import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import queryKeys from '@/constants/queryKeys';
import useAdminContentDetail from '@/hooks/content/useAdminContentDetail';
import useContentImageUpload from '@/hooks/content/useContentImageUpload';
import useDeleteContent from '@/hooks/content/useDeleteContent';
import usePublishContent from '@/hooks/content/usePublishContent';
import useUpdateContent from '@/hooks/content/useUpdateContent';
import useToast from '@/hooks/useToast';
import BrandedContentWizard, { TBrandedFormMode } from '@/modules/content/branded/views/BrandedContentWizard';
import {
  buildContentDetails,
  collectImageUploads,
  mapContentToFormValues,
} from '@/modules/content/branded/contentPayload';
import { TBrandedFormSubmit } from '@/modules/content/branded/types';
import { ContentStatusBadge } from '@/modules/content/common/contentDisplay';
import { createMuxUploadUrl, setYoutubeVideoSource } from '@/services/api/requests/content';
import { uploadFileToMux } from '@/services/muxUpload';
import { TCustomAxiosError } from '@/services/api';
import colors from '@/theme/colors';
import { Center, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useState } from 'react';

const EditBrandedContent = () => {
  const { contentId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const deleteDisclosure = useDisclosure();

  const { isLoading, isPending, isSuccess, data } = useAdminContentDetail(contentId, {
    pollWhileProcessing: true,
  });
  const { mutateAsync: updateContent } = useUpdateContent(contentId);
  const { uploadThumbnailAsync, uploadArticleImageAsync } = useContentImageUpload();
  const { publish, unpublish, isPublishing, isUnpublishing } = usePublishContent(contentId);
  const { isPending: isDeleting, mutate: deleteContent } = useDeleteContent(contentId, () =>
    navigate({ to: '/content/branded-content' }),
  );

  const [isSaving, setIsSaving] = useState(false);

  // Only block on the initial load — background polling (Mux status) must not unmount
  // the form/wizard, so isRefetching is deliberately excluded.
  const showLoader = isLoading || isPending;
  const status = data?.status;
  const isRemoved = status === 'removed';
  const isWithdrawn = status === 'withdrawn';
  // Both soft-deleted states are terminal for authoring: the backend refuses
  // updates, publishing and a second delete on either.
  const isLocked = isRemoved || isWithdrawn;
  const isPublished = status === 'published';
  const isDraft = status === 'draft';

  // Video / Mux processing state — gates publishing.
  const videoDetails = (data?.details ?? {}) as { source?: string; mux?: { status?: string } };
  const isVideo = data?.type === 'video';
  const isMuxSource = isVideo && videoDetails.source === 'mux';
  const muxStatus = videoDetails.mux?.status;
  const isVideoPublishable = !isVideo || videoDetails.source === 'youtube' || muxStatus === 'ready';

  const goToList = () => navigate({ to: '/content/branded-content' });

  const handleSave = async (mode: TBrandedFormMode, { values, thumbnailFile }: TBrandedFormSubmit) => {
    if (!data) return;
    setIsSaving(true);
    try {
      await updateContent({
        title: values.title,
        description: values.description,
        domains: values.domains,
        subdomains: values.subdomains,
        details: buildContentDetails(data.type, values),
      });

      if (thumbnailFile) {
        await uploadThumbnailAsync({ id: contentId, file: thumbnailFile });
      }
      for (const { sectionId, subsectionId, file } of collectImageUploads(values)) {
        await uploadArticleImageAsync({ id: contentId, file, sectionId, subsectionId });
      }

      // Video source can only be set/switched while the item is a draft.
      if (data.type === 'video' && isDraft) {
        if (values.videoSource === 'youtube' && values.youtubeUrl.trim()) {
          await setYoutubeVideoSource(contentId, values.youtubeUrl.trim());
        } else if (values.videoSource === 'mux' && values.videoFile) {
          const { uploadUrl } = await createMuxUploadUrl(contentId);
          await uploadFileToMux(uploadUrl, values.videoFile);
        }
      }

      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.byId, contentId] });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.all] });
      toast({ status: 'success', title: 'Success', description: 'Changes saved.' });
      void mode;
    } catch (error) {
      toast({
        status: 'error',
        title: 'Error saving content',
        description: (error as TCustomAxiosError)?.errors?.[0]?.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const headerActions = data ? (
    <HStack gap="16px">
      {isPublished ? (
        <CustomButton
          label="Unpublish"
          isTransparent
          labelColor="background.blueGray"
          labelHoverColor="extra.black"
          isLoading={isUnpublishing}
          onClick={() => unpublish()}
        />
      ) : (
        !isLocked && (
          <CustomButton
            label="Publish"
            backgroundColor="extra.teal"
            labelColor="extra.white"
            labelHoverColor="extra.white"
            isLoading={isPublishing}
            disabled={!isVideoPublishable}
            disabledExplanation="The video is still processing in Mux and can't be published yet."
            onClick={() => publish()}
          />
        )
      )}
      {!isLocked && (
        <CustomButton
          label="Delete"
          backgroundColor="extra.red"
          labelColor="extra.white"
          labelHoverColor="extra.white"
          onClick={deleteDisclosure.onOpen}
        />
      )}
    </HStack>
  ) : undefined;

  return (
    <SidebarItemViewTemplate title="Edit MyOmnia Content" customButton={headerActions}>
      <HStack justify="space-between" width="100%" marginTop="20px">
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIconPaths.leftArrow}
          labelColor={colors.background.blueGray}
          labelHoverColor="extra.black"
          label="Back to Branded Content"
          hoverBackgroundColor="transparent"
          borderHoverColor="transparent"
          onClick={goToList}
          labelStyle={{ marginBottom: '5px', fontSize: '16px' }}
        />
        {data && <ContentStatusBadge status={data.status} />}
      </HStack>

      {!showLoader && isMuxSource && muxStatus && muxStatus !== 'ready' && (
        <VStack align="stretch" width="100%" maxWidth="760px" marginTop="8px">
          <Text
            fontSize="14px"
            fontWeight={600}
            color={muxStatus === 'errored' ? 'extra.red' : 'text.mediumBlue'}
            backgroundColor={muxStatus === 'errored' ? 'extra.veryLightRed' : 'background.lightBlue'}
            borderRadius="8px"
            padding="10px 14px"
          >
            {muxStatus === 'errored'
              ? 'Video processing failed in Mux. Re-upload the file to try again.'
              : 'Video is processing in Mux. This page updates automatically; publishing unlocks once it’s ready.'}
          </Text>
        </VStack>
      )}

      {showLoader && (
        <Center width="100%" paddingY="40px">
          <LoadingLogo />
        </Center>
      )}

      {!showLoader && isSuccess && data && isRemoved && (
        <VStack align="flex-start" width="100%" marginTop="20px">
          <Text color="text.mediumBlue">
            This content has been removed. Restore it from Content Moderation to edit it again.
          </Text>
        </VStack>
      )}

      {!showLoader && isSuccess && data && isWithdrawn && (
        <VStack align="flex-start" width="100%" marginTop="20px">
          <Text color="text.mediumBlue">
            This content has been deleted. It stays readable in Content Moderation for the retention
            window, but it can no longer be edited, published or restored.
          </Text>
        </VStack>
      )}

      {!showLoader && isSuccess && data && !isLocked && (
        <BrandedContentWizard
          type={data.type}
          defaultValues={mapContentToFormValues(data)}
          // Match the table: fall back to the effective thumbnail (Mux/YouTube/section
          // image) when no custom thumbnail was uploaded.
          defaultThumbnailUrl={data.effectiveThumbnailUrl ?? data.thumbnailUrl}
          isSubmitting={isSaving}
          onExit={goToList}
          onSubmit={handleSave}
          // A published item must keep satisfying publish-time validation; save is
          // reachable from both the editor and the preview step.
          secondaryAction={{
            key: 'save',
            label: 'Save changes',
            mode: isPublished ? 'publish' : 'draft',
            backgroundColor: 'primary.500',
          }}
          primaryAction={{
            key: 'save-preview',
            label: 'Save changes',
            mode: isPublished ? 'publish' : 'draft',
            backgroundColor: 'primary.500',
          }}
        />
      )}

      <ConfirmationModal
        isOpen={deleteDisclosure.isOpen}
        title="Delete Content"
        description="This takes the content down immediately — it leaves the feed and can no longer be edited, published or restored. It is not erased: it is retained as withdrawn content and stays readable in Content Moderation for the retention window."
        confirmButtonText="Delete"
        confirmButtonColor="extra.red"
        confirmLoading={isDeleting}
        onCancel={deleteDisclosure.onClose}
        onConfirm={() => deleteContent()}
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/content/branded-content/$contentId')({
  component: EditBrandedContent,
});
